// MIDAS V3 — Composer (Phase 2 + Phase 7 attach port)
//
// Card-style composer per spec §7 + handoff chat-window.jsx (lines 774-799):
//   • max-width 820, centered
//   • padding 8×12, radius 12, var(--shadow)
//   • flex row align-items: flex-end
//   • MachinePill left (bottom-aligned with paddingBottom 4)
//   • AutoResizeTextarea grows 1→6 lines, then internal scroll
//   • Image preview chip row above textarea (when attached)
//   • `+` attach button 30×30 — NOW WIRED (Phase 7 port from V2):
//       - Disabled when no machineContext (mirrors V2 `imageUploadDisabled`)
//       - Opens hidden <input type="file"> on click
//       - Resizes image (max 2048px, JPEG q=0.85) before dispatching
//       - Image rides along on the existing sendMessage thunk `image?: File`
//   • `☰` composer menu 30×30 (COMPOSER-MENU, left of ＋) — hosts the
//     per-thread model picker in a popover (ComposerMenuV3); `.cc`-only,
//     accent dot when a model override is active
//   • `↑` primary send button 34×30
//   • Bottom hint row (mono 10.5, faint): `{machine} · ↵ send · ⇧↵ newline`
//     left + "Midas can make mistakes — verify critical values." right
//
// Dispatches `sendMessage` thunk from shared chatBotSlice. The thunk already
// accepts `image?: File` (used by V2) — no backwards-compatible extension
// needed, no new thunk added.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import {
  clearError,
  sendMessage,
  setNewThreadPending,
} from "../../../../shared/chatBot/chatBotSlice";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { Roles } from "../../../../account/models/AccountModels";
import AutoResizeTextareaV3, {
  AutoResizeTextareaV3Handle,
} from "./AutoResizeTextareaV3";
import ComposerMenuV3 from "./ComposerMenuV3";
import MachinePillV3 from "./MachinePillV3";
import { DRAFT_THREAD_KEY } from "../../midasV3Slice";
import { MIDAS_V3_FOCUS_COMPOSER_EVENT } from "./artifacts/FullscreenArtifactV3";
import {
  ACCEPTED_IMAGE_EXTENSIONS,
  ACCEPTED_IMAGE_TYPES,
  MAX_IMAGE_SIZE_BYTES,
  resizeImageForUpload,
} from "./v3-utils/resizeImageForUpload";

const MAX_CHARS = 5000;

// Design handoff (multichat v3.3): composer card < ~560px → the machine pill
// yields (hidden); machine context remains visible in the column's pill
// header. The hint row already wraps.
const PILL_YIELD_WIDTH = 560;

export interface ChatInputAreaV3Props {
  /** MODEL-PICKER P3: when set, this composer belongs to an ATTACHED column.
   *  Sends target the column's thread (distinct thread_id on EVERY message —
   *  spec §6 hard requirement) with the column's OWN machine scope; global
   *  machineContext / activeThreadId / newThreadPending are not touched. */
  column?: {
    threadId: string;
    machineId: string | null;
    machineName?: string;
  };
}

const ChatInputAreaV3: React.FC<ChatInputAreaV3Props> = ({ column }) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");
  const machineContext = useAppSelector((s) => s.chatBot.machineContext);
  const activeThreadId = useAppSelector((s) => s.midasPage.activeThreadId);
  const isBotAvailable = useAppSelector((s) => s.chatBot.isBotAvailable);
  const currentUser = useAppSelector((s) => s.account.user);

  // Thread-scoped streaming flag (matches v2 selector pattern). When the
  // target thread is streaming we disable the send button so a second message
  // can't queue mid-stream. Column composers read THEIR thread's buffer.
  const activeThreadIsStreaming = useAppSelector((s) => {
    const tid = column ? column.threadId : s.midasPage.activeThreadId;
    if (!tid) return s.chatBot.isLoading;
    return s.chatBot.threadBuffers[tid]?.isLoading || false;
  });

  // ── MODEL-PICKER §6 draft guard (waiting affordance) ──
  // (a) While the primary DRAFT's first turn is in flight (no thread_id on
  //     the wire until thread_created), attached-column composers wait.
  // (b) While any attached column streams, a primary send is blocked IF the
  //     primary has no thread_id yet (a draft send would fall back to the
  //     bot's shared user-level session → machine-context bleed).
  // Normal primary sends with a thread_id are unaffected. The sendMessage
  // thunk has a matching hard guard for the race window.
  const draftStreamInFlight = useAppSelector((s: any) => {
    const cols = s.midasAttach?.columns ?? [];
    if (cols.length === 0) return false;
    const threads = s.midasPage?.threads ?? [];
    return threads.some(
      (t: any) =>
        t.isOptimistic === true &&
        s.chatBot.threadBuffers[t.id]?.isLoading === true
    );
  });
  const anyColumnStreaming = useAppSelector((s: any) => {
    const cols = s.midasAttach?.columns ?? [];
    return cols.some(
      (c: any) => s.chatBot.threadBuffers[c.threadId]?.isLoading === true
    );
  });
  const draftGuardBlocked = column
    ? draftStreamInFlight
    : !activeThreadId && anyColumnStreaming;

  const [value, setValue] = useState("");
  const [attachedImage, setAttachedImage] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [snackError, setSnackError] = useState<string | null>(null);
  const textareaRef = useRef<AutoResizeTextareaV3Handle | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ── Narrow-card pill yield (design handoff, multi-column view) ──
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [pillYields, setPillYields] = useState(false);
  useEffect(() => {
    const el = cardRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setPillYields(entry.contentRect.width < PILL_YIELD_WIDTH);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── TAJMAC-PILOT confinement invariant (MIDAS_PILOT_ROLE_SPEC.md §5) ──
  //
  // The machine this composer would actually send as: column composers carry their
  // own scope, the primary composer uses the global machineContext.
  const effectiveMachineId = column
    ? column.machineId || undefined
    : machineContext?.machineId;

  // A MidasPilot must NEVER produce an unscoped (platform / fleet-wide) chat.
  // Hiding the Platform card in NewChatDialogV3 only blocks CHOOSING that mode, and
  // MidasV3Layout's auto-scope is a RACE — on a cold /midas load it returns while the
  // machine list is still fetching, and no-ops entirely if the tenant does not hold
  // exactly one machine (or the fetch fails). During that window `machineContext` is
  // null, so a send would pass `machineId: undefined` → `machine_id: null` → exactly
  // the fleet-wide mode the role excludes.
  //
  // This flag is the INVARIANT. It is enforced twice, deliberately:
  //   1. in handleSubmit, before any dispatch (the guarantee — holds regardless of
  //      what `disabled` is ever refactored into);
  //   2. in `disabled` below (the UX half — so the guard is never user-visible in the
  //      happy path, where auto-scope lands in well under a second).
  // It also supersedes the previously-accepted 0/2+ machine residual: in that shape
  // the composer now fails closed instead of sending unscoped.
  const isMidasPilot = currentUser?.role === Roles.MIDAS_PILOT;
  const pilotScopeBlocked = isMidasPilot && !effectiveMachineId;

  const disabled =
    isBotAvailable === false ||
    activeThreadIsStreaming ||
    draftGuardBlocked ||
    pilotScopeBlocked;
  // Mirrors V2 MidasChatAreaV2 line 267: `imageUploadDisabled={!machineContext}`.
  // No machine in context → bot has nowhere to attach the image, so we disable.
  // Column composers key on the COLUMN's machine scope.
  const imageUploadDisabled = column ? !column.machineId : !machineContext;

  // MODEL-PICKER: per-thread picker key. Primary drafts key on "draft" and
  // are migrated to the real thread id by midasV3Slice's extraReducers.
  const pickerThreadKey = column
    ? column.threadId
    : activeThreadId || DRAFT_THREAD_KEY;

  // MIDAS-V3-P3: Listen for "focus composer with prefilled text" events
  // dispatched by FullscreenArtifactV3 when the user clicks "+ Spýtať sa
  // MIDAS". Loose coupling via CustomEvent so neither component imports the
  // other's internals.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ text: string }>).detail;
      if (!detail || typeof detail.text !== "string") return;
      setValue((current) => {
        // Only overwrite if empty so we don't clobber an in-progress draft.
        if (current && current.length > 0) return current;
        return detail.text;
      });
      // Focus on the next frame so the underlying textarea is laid out.
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    };
    window.addEventListener(MIDAS_V3_FOCUS_COMPOSER_EVENT, handler);
    return () =>
      window.removeEventListener(MIDAS_V3_FOCUS_COMPOSER_EVENT, handler);
  }, []);

  // Generate preview URL for the attached image. Revoke on replace / unmount
  // so we don't leak blob URLs. Mirrors V2 useEffect behaviour.
  useEffect(() => {
    if (!attachedImage) {
      setImagePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(attachedImage);
    setImagePreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [attachedImage]);

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim();
    // Allow image-only sends (empty text + attached image). Matches V2.
    if (!trimmed && !attachedImage) return;

    // HARD GUARD — the pilot confinement invariant, checked BEFORE any dispatch so no
    // thunk is ever called unscoped. Deliberately redundant with `disabled` below:
    // `disabled` is UX and could be refactored, this is the guarantee. Returns without
    // clearing `value`, so the user's text survives until auto-scope lands and the
    // composer re-enables.
    if (pilotScopeBlocked) return;

    if (disabled) return;

    dispatch(clearError());

    if (column) {
      // MODEL-PICKER §6: attached-column send — ALWAYS carries the column's
      // distinct thread_id (hard requirement; a no-thread_id message falls
      // back to the bot's shared user-level session and would bleed machine
      // context across columns). Machine id passed EXPLICITLY — the thunk
      // will not fall back to the primary chat's global machineContext.
      // The thread's model_override is resolved inside the thunk at
      // dispatch time (spec §4A point 6).
      dispatch(
        sendMessage({
          message: trimmed,
          machineId: column.machineId || undefined,
          threadId: column.threadId,
          image: attachedImage || undefined,
          originThreadId: column.threadId,
        })
      );
    } else {
      if (!activeThreadId) {
        dispatch(setNewThreadPending());
      }
      dispatch(
        sendMessage({
          message: trimmed,
          machineId: machineContext?.machineId,
          threadId: activeThreadId || undefined,
          image: attachedImage || undefined,
          originThreadId: activeThreadId || undefined,
        })
      );
    }

    setValue("");
    setAttachedImage(null);
    // Reset the textarea height after React flushes the state.
    requestAnimationFrame(() => {
      textareaRef.current?.reset();
    });
  }, [
    dispatch,
    value,
    disabled,
    pilotScopeBlocked,
    activeThreadId,
    machineContext,
    attachedImage,
    column,
  ]);

  const handleAttachClick = useCallback(() => {
    if (disabled || imageUploadDisabled) return;
    fileInputRef.current?.click();
  }, [disabled, imageUploadDisabled]);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Reset the input so the same file can be re-selected after a remove.
      e.target.value = "";
      if (!file) return;

      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        setSnackError(
          translate("midas.imageUpload.unsupportedFormat") as string
        );
        return;
      }

      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        setSnackError(translate("midas.imageUpload.imageTooLarge") as string);
        return;
      }

      const resized = await resizeImageForUpload(file);
      setAttachedImage(resized);
    },
    [translate]
  );

  const handleRemoveImage = useCallback(() => {
    setAttachedImage(null);
  }, []);

  // Build the scope label (machine model + brand or platform). Column
  // composers show the COLUMN's machine name.
  const machineName = (() => {
    if (column) return column.machineName || "";
    if (!machineContext) return "";
    const model = machineContext.model?.trim() || "";
    const brand = machineContext.manufacturerName?.trim() || "";
    if (model) return brand ? `${brand} ${model}` : model;
    if (machineContext.internalNumber) return machineContext.internalNumber;
    if (machineContext.serialNumber) return `SN ${machineContext.serialNumber}`;
    return "";
  })();

  const waitingHint = translate("midas.v3.attach.waitingForThread", {
    defaultValue: "Waiting for the new chat to be created…",
  }) as string;

  const placeholder = translate("midas.v3.composer.placeholder", {
    defaultValue: "Ask Midas anything…",
  }) as string;

  const sendHint = translate("midas.v3.composer.sendHint", {
    defaultValue: "↵ send · ⇧↵ newline",
  }) as string;

  const disclaimer = translate("midas.v3.composer.disclaimer", {
    defaultValue: "Midas can make mistakes — verify critical values.",
  }) as string;

  const attachLabel = imageUploadDisabled
    ? (translate("midas.imageUpload.selectMachineHint") as string)
    : (translate("midas.imageUpload.attachImage") as string);

  const canSend =
    (value.trim().length > 0 || attachedImage !== null) && !disabled;

  return (
    <div
      style={{
        borderTop: "1px solid var(--border)",
        // Compact gutters inside attached columns (design handoff).
        padding: column ? "12px 16px" : "14px 32px",
        background: "var(--bg)",
      }}
    >
      {/* Hidden file input — no `capture` attribute (BUG-MIDAS-MOBILE-0414) */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_EXTENSIONS}
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      <div
        ref={cardRef}
        style={{
          maxWidth: 820,
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: "8px 12px",
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          boxShadow: "var(--shadow)",
        }}
      >
        {/* Image preview chip (only when attached) */}
        {attachedImage && imagePreviewUrl && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "2px 0",
            }}
          >
            <div
              style={{
                position: "relative",
                width: 48,
                height: 48,
                flexShrink: 0,
                borderRadius: 8,
                overflow: "hidden",
                border: "1px solid var(--border)",
                background: "var(--card-2)",
              }}
            >
              <img
                src={imagePreviewUrl}
                alt={attachedImage.name}
                style={{
                  display: "block",
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
              <button
                type="button"
                onClick={handleRemoveImage}
                aria-label={
                  translate("midas.v3.composer.removeAttachmentAria", {
                    defaultValue: "Remove attached image",
                  }) as string
                }
                title={
                  translate("midas.v3.composer.removeAttachmentAria", {
                    defaultValue: "Remove attached image",
                  }) as string
                }
                style={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  width: 18,
                  height: 18,
                  padding: 0,
                  border: "none",
                  borderRadius: 9,
                  background: "rgba(0,0,0,0.7)",
                  color: "#fff",
                  cursor: "pointer",
                  fontFamily: "var(--font-sans)",
                  fontSize: 11,
                  lineHeight: 1,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                ×
              </button>
            </div>
            <span
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: 12,
                color: "var(--muted)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 240,
              }}
            >
              {attachedImage.name}
            </span>
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: 8,
          }}
        >
          {/* Machine pill — yields (hidden) when the composer card is
              narrower than ~560px (multi-column view); machine context stays
              visible in the column's pill header. Column composers show the
              COLUMN's machine, never the primary chat's context. */}
          {!pillYields && (
            <div style={{ paddingBottom: 4, flexShrink: 0 }}>
              <MachinePillV3
                compact
                platform={column ? !column.machineId : false}
                machineOverride={
                  column && column.machineId
                    ? { name: column.machineName || "" }
                    : undefined
                }
              />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <AutoResizeTextareaV3
              ref={textareaRef}
              value={value}
              onChange={setValue}
              onSubmit={handleSubmit}
              placeholder={placeholder}
              disabled={disabled}
              maxLength={MAX_CHARS}
              ariaLabel={placeholder}
            />
          </div>
          {/* COMPOSER-MENU (2026-07-15): the model picker moved OUT of the
              input row into the ☰ popover (the pipeline pills that shared it
              were removed — BUG 11, 2026-10-02) — the freed
              space goes to the textarea (flex: 1). Placement: to the LEFT of
              the ＋ attach button. Same threadKey plumbing as before — one
              mount here covers the primary composer AND every attached
              column (this component renders each column's composer), so the
              thread_id-per-column rule is untouched. `.cc`-only: renders
              null off-`.cc`, composer row pixel-identical to today. */}
          <ComposerMenuV3
            threadKey={pickerThreadKey}
            disabled={isBotAvailable === false}
          />
          <button
            type="button"
            onClick={handleAttachClick}
            disabled={disabled || imageUploadDisabled}
            aria-label={attachLabel}
            title={attachLabel}
            style={{
              width: 30,
              height: 30,
              padding: 0,
              flexShrink: 0,
              background: "transparent",
              color:
                disabled || imageUploadDisabled
                  ? "var(--faint)"
                  : "var(--muted)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              cursor:
                disabled || imageUploadDisabled ? "not-allowed" : "pointer",
              display: "grid",
              placeItems: "center",
              fontFamily: "var(--font-sans)",
              fontSize: 16,
              opacity: disabled || imageUploadDisabled ? 0.5 : 1,
              transition:
                "background 0.12s ease, color 0.12s ease, border-color 0.12s ease, opacity 0.12s ease",
            }}
          >
            ＋
          </button>
          <button
            type="button"
            onClick={canSend ? handleSubmit : undefined}
            disabled={!canSend}
            aria-label={
              translate("midas.v3.composer.sendAria", {
                defaultValue: "Send message",
              }) as string
            }
            style={{
              width: 34,
              height: 30,
              padding: 0,
              flexShrink: 0,
              background: canSend ? "var(--accent)" : "var(--card-2)",
              color: canSend ? "white" : "var(--faint)",
              border:
                "1px solid " + (canSend ? "var(--accent)" : "var(--border)"),
              borderRadius: 8,
              cursor: canSend ? "pointer" : "not-allowed",
              display: "grid",
              placeItems: "center",
              fontFamily: "var(--font-sans)",
              fontSize: 16,
              transition:
                "background 0.12s ease, color 0.12s ease, border-color 0.12s ease",
            }}
          >
            ↑
          </button>
        </div>
      </div>
      <div
        style={{
          maxWidth: 820,
          margin: "6px auto 0",
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          fontSize: 10.5,
          color: "var(--faint)",
          fontFamily: "var(--font-mono)",
          letterSpacing: "0.04em",
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "100%",
            // Waiting affordance (draft guard) reads accent so the block
            // reason is visible without a toast.
            color: draftGuardBlocked ? "var(--accent)" : undefined,
          }}
        >
          {draftGuardBlocked
            ? waitingHint
            : machineName
            ? `${machineName} · ${sendHint}`
            : sendHint}
        </span>
        <span style={{ textAlign: "right" }}>{disclaimer}</span>
      </div>

      {/* Error toast — plain HTML, no MUI Snackbar. Same UX as V2. */}
      {snackError && (
        <div
          role="alert"
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 50,
            padding: "8px 16px",
            borderRadius: 8,
            border: "1px solid #FCA5A5",
            background: "#FEF2F2",
            color: "#B91C1C",
            fontFamily: "var(--font-sans)",
            fontSize: 13,
            boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <span>{snackError}</span>
          <button
            type="button"
            onClick={() => setSnackError(null)}
            aria-label={
              translate("midas.v3.composer.dismissErrorAria", {
                defaultValue: "Dismiss",
              }) as string
            }
            style={{
              border: "none",
              background: "transparent",
              color: "inherit",
              cursor: "pointer",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              textTransform: "uppercase",
              opacity: 0.7,
              padding: 0,
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default ChatInputAreaV3;
